import csiCard, { CardSpecs } from "csi/card/_base";
import configPreferences from "csi/config/preferences";
import { CSS_TABLE_COLUMNS } from "csi/constants";
import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import SpecBuilder from "csi/core/SpecBuilder";
import def from "csi/def";
import { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import PreferencePathConstructor, { PreferencePath } from "csi/def/PreferencePath";
import { Rule } from "csi/def/Rule";
import { Type } from "csi/def/Type";
import { View } from "csi/def/View";
import { create } from "csi/def/ViewFactory";
import Specs from "csi/heading/Specs";
import locale from "csi/locale";
import csi from "csi/main";
import { flattenAttributeKeys } from "csi/middleware/attributes/attributeKeysService";
import CustomViewAttributesStore, { CustomViewAttributesOptions } from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import { ICustomViewOptions } from "csi/middleware/customView/options/CustomViewOptions";
import { ATTR_IMAGES, DEFAULT_IMAGE_LABEL } from "csi/model/image";
import PreferenceView from "csi/prefs/PreferenceView";
import { PropertyView } from "csi/property/View";
import { getAttribute, getClass, hasFormDefinitionConfigNodeForType, hasReactFormForType } from "csi/reflection/reflection";
import ReflectionClass from "csi/reflection/ReflectionClass";
import reflectionHints from "csi/reflectionHints";
import TableRelationshipConstructor from "csi/table/Relationship";
import { TableView } from "csi/table/View";
import { AccessRule, ICache } from "csi/typings";
import { ViewWidget } from "csi/view/Widget";
import xml from "csi/xml";
import { ILocaleProvider } from "designSystem/locale";
import { compact, extend } from "lodash";
import { ICustomViewAttribute } from "src/c8-platform/type/customView";
import { C8AttributeId, C8Node, C8NodeType } from "src/c8-platform/type/node";
import { C8AttributeValue } from "src/c8-platform/type/value";

// TODO: Move utility here once csi/def no longer needs it
export { getViewAccessRights } from "csi/state/viewUtils";

export const TEMPLATE_VIEW_ID = "__Template__";
export const TABLE_ID = "__Table__";

/**
 * Return true if attribute on node can be edited inline (even if form only);
 * note that the path has already been traversed, ending at the given node
 * @return pair of booleans indicating if inline editable, and if so, if on form only
 */
export function attributeIsInlineEditable(node: C8Node, id: string, path?: string): [boolean, boolean] {
	// if attribute not in model, then don't enforce any rules
	const rca = getAttribute(node, id);
	if (!rca) {
		return [!!id, false];
	}

	const { isCreateOnly, isEditingDisabled, isFormOnly, isTableViewOnly } = rca;

	// inline editing not supported in any view
	if (isEditingDisabled || isTableViewOnly) {
		return [false, false];
	}

	// attributes along a special path are not edited in default form,
	// so by default they are inline editable
	if (reflectionHints.isSpecialPath(node.$Type, path)) {
		return [true, false];
	}

	// form only attributes are only editable if there is a form
	if (isFormOnly && !hasFormDefinitionForType(node.$Type)) {
		return [false, false];
	}

	return [!isCreateOnly, isFormOnly];
}

/**
 * Add dependency paths necessary to render given specs to array
 */
export function collectSpecDependencyPaths(specs: Specs, paths?: string[]): string[] {
	paths ||= [];

	specs.traverse(true, (spec, subSpec) => {
		// we are traversing sub-specs here, so no need for getHeadingDependencyPaths to manage sub-headings for groups
		const s = subSpec || spec;
		const { heading } = s;
		if (heading) {
			def.getHeadingDependencyPaths(s.heading, paths, false, s.getStandardQueryPath(), s.isGroup);
		}
	});

	return paths;
}

/**
 * Return custom view attributes store for view
 */
export function createAttributesStore(view: ViewWidget, loc: ILocaleProvider, omitSelections?: boolean): CustomViewAttributesStore | undefined {
	const levels = view.getCustomViewAttributeLevels?.();
	if (!levels) {
		return;
	}

	// determine options for store
	const attributesOptions: CustomViewAttributesOptions = {};
	view.onCustomViewAttributeOptions(attributesOptions);

	const attributesStore = new CustomViewAttributesStore(loc, attributesOptions);
	levels.forEach(attributesStore.addLevel);

	if (!omitSelections) {
		attributesStore.addSelections(view.attributeKeys, view.hiddenKeys);
	}

	return attributesStore;
}

/**
 * Generate and return empty Spec
 */
export function createEmptySpec(): Spec {
	return new Spec({
		heading: new Heading(),
	});
}

/**
 * Generate attributes keys from view headings
 */
export function generateAttributeKeysFromHeadings(headings: Heading[], specBuilders: SpecBuilder[]): ICustomViewAttribute[] {
	const attributes: ICustomViewAttribute[] = [];
	const mainSpecBuilder = specBuilders[0];

	for (const h of headings) {
		const { bFixedKey, isHidden, isMatrixGroup } = h;
		let attribute: ICustomViewAttribute;

		if (isMatrixGroup) {
			// matrices exist on main level, so use first spec builder
			const spec = mainSpecBuilder.getSpecForHeading(h, h.m_subheadings);
			if (spec) {
				const { key, matrixTargetClass, name, subs } = spec;
				const visibleSubs = subs.filter(({ heading, ra }) => !heading.isHidden && (!ra || ra.isEnabled));
				
				attribute = {
					groupKey: key.toString(),
					keys: visibleSubs.map((spec) => spec.uniqueId),
					matrixTarget: matrixTargetClass.type,
					name,
				};
			} else {
				console.warn("Cannot create spec for matrix: ", h);
			}
		} else {
			// for all others, iterate through spec builders to find right key - for views with multiple levels, attribute could
			// exist on any level. in the first pass here we set validate to true to be as exact as possible. If we do not find a
			// "valid" key, then we will assume the key is written for the main level of the table and create a key using it.
			let key: Key;

			if (specBuilders.length > 1) {
				specBuilders.some(({ getKeyForHeading }) => {
					key = getKeyForHeading(h, null, true);
					return !!key;
				});
			}

			if (!key) {
				// if key is not unique in the model in any one of the spec builders, then use key from main spec builder as best guess
				key = mainSpecBuilder.getKeyForHeading(h);
			}

			// special case handling for default image heading
			if (key.id === ATTR_IMAGES && key.index === DEFAULT_IMAGE_LABEL) {
				key = key.copy({
					index: null,
				});
			}

			if (!key.attributeIsDisabled) {
				attribute = key.toString();
			}
		}

		if (attribute && !bFixedKey && !isHidden) {
			attributes.push(attribute);
		}
	}

	return attributes;
}

/**
 * Generate group spec
 */
export function generateGroupSpec(
	key: Key,
	name: string,
	subs: Spec[],
	onGroupSpecs?: (groupSpecs: Spec[]) => void,
): Spec {
	const spec = new Spec({
		css: CSS_TABLE_COLUMNS,
		heading: new Heading(),
		key,
		name,
		subs,
	});

	onGroupSpecs?.([spec]);

	return spec;
}

/**
 * Convert attribute specs in view to full set of column specs including matrix columns
 */
export function generateColumnSpecsFromAttributeSpecs(ctx: Context, columnSpecs: Specs, attributeSpecs: Specs): void {
	attributeSpecs.traverse(false, (spec) => {
		const { heading, isMatrix } = spec;

		if (isMatrix) {
			const mp = heading.getMatrixProcessor();
			if (mp) {
				const matrixSpecs = mp.getColumnSpecs(ctx, heading);

				for (const matrixSpec of matrixSpecs) {
					columnSpecs.add(matrixSpec);
				}
			}
		} else {
			columnSpecs.add(spec);
		}
	});
}

/**
 * Resolve view specs from attribute keys (which will be normal flow once using new custom view dialog 7.9.1)
 */
export function generateSpecsFromAttributeKeys(specs: Specs, specBuilder: SpecBuilder, attributeKeys: ICustomViewAttribute[]): void {
	const { getSpecForKey } = specBuilder;

	for (const groupOrKey of attributeKeys) {
		let spec: Spec;

		if (typeof groupOrKey === "string") {
			// single attribute
			spec = getSpecForKey(groupOrKey, null, null, null, true);
			
			if (!spec?.isAvailable) {
				spec = null;
			}
		} else {
			// attribute or matrix group
			const { groupKey, keys, matrixTarget, name } = groupOrKey;

			if (matrixTarget) {
				// matrix group
				spec = specBuilder.getMatrixGroupSpecForKey(groupKey, keys);
			} else {
				// attribute group
				const subs = [];

				for (const key of keys) {
					const spec = getSpecForKey(key);
					if (spec?.isAvailable) {
						subs.push(spec);
					}
				}

				if (subs.length) {
					let key: Key;

					if (groupKey) {
						key = new Key({ dpkey: groupKey });
					} else {
						key = new Key({
							extendedPath: specBuilder.defaultSourcePath,
							name,
						});
					}

					spec = generateGroupSpec(key, name, subs);
				}
			}
		}

		if (spec) {
			specs.add(spec);
		}
	}
}

/**
 * Generate specs from those starting at a different class. The main builder is the one for the main level of the table (the primary target class).
 * Other levels, like the root level if being shown or lower levels of the table are based on the main level specs.
 */
export function generateSpecsForLevel(
	mainSpecs: Specs,
	specBuilder: SpecBuilder,
	specs: Specs,
	isRootLevel: boolean,
	onGroupSpecs: (groupSpecs: Spec[]) => void,
): void {
	const { defaultSourcePath, getCachedSpec, getKeyForSourceClass } = specBuilder;

	const getSpec = (spec: Spec): Spec => {
		const { heading, key, params } = spec;

		// reevaluate key for new starting class and generate spec if heading is in model and not customized for the view (with renderer class)
		if (heading && (heading.m_id || heading.m_prop) && !heading.classRenderer) {
			const newKey = getKeyForSourceClass(key);
			if (newKey) {
				spec = getCachedSpec(newKey, true, false, { params });
			} else {
				spec = null;
			}
		}

		return spec ?? createEmptySpec();
	};

	const getEmptyMatrixGroup = (spec: Spec): Spec => {
		const { name, subs } = spec;
		const newKey = new Key({
			extendedPath: defaultSourcePath,
			name,
		});
		const newSubs = subs?.map(() => createEmptySpec());
		return generateGroupSpec(newKey, name, newSubs, onGroupSpecs);
	};

	mainSpecs.traverse(false, (spec) => {
		const { heading, isGroup, isMatrix, key, name, subs } = spec;
		const mp = heading?.getMatrixProcessor();
		let newSpec = spec;

		if (isMatrix) {
			if (isRootLevel && !mp?.bAllowOnRootNode) {
				// do not allow matrices for root level unless explicitly allowed, so replace with empty specs
				if (isGroup) {
					newSpec = getEmptyMatrixGroup(spec);
				} else {
					newSpec = createEmptySpec();
				}
			} else {
				// otherwise don't re-evaluate matrix specs per level - they apply mainly to main level (or subsequent levels with same model)
			}
		} else if (isGroup) {
			// convert group's keys to use new start class;
			// for now reuse group key as it shouldn't really matter in rendering at new level
			const newSubs = subs.map((sub) => getSpec(sub));
			newSpec = generateGroupSpec(key, name, newSubs, onGroupSpecs);
		} else {
			// convert single spec to use new start class
			newSpec = getSpec(spec);
		}

		specs.add(newSpec);
	});
}

/** convert Rule definition to interface */
export function getAccessRule(rule: Rule): AccessRule {
	// NOTE: eventually this may not be needed, but need Rule without functions so no dependency on rollup
	const {
		bApplyToForm,
		m_exp,
		m_id1,
		m_id2,
		m_op,
		m_path,
		m_setting,
		m_value,
	} = rule;

	return {
		applyToForm: bApplyToForm,
		exp: m_exp,
		id1: m_id1,
		id2: m_id2,
		op: m_op,
		path: m_path,
		setting: m_setting,
		value: m_value,
	};
}

/**
 * Return attribute keys for Filter Attributes based on latest configuration
 */
export function getAttributeCardSpecsForType(type: C8NodeType, customView?: C8Node): CardSpecs {
	const { attributeKeys } = loadCustomViewAttributeKeys(customView);
	const keys = attributeKeys ? flattenAttributeKeys(attributeKeys).map((dpkey) => new Key({ dpkey })) : null;
	return csiCard.getAttributeCardSpecs(type, keys);
}

/** return preference path objects for paths used in attribute card for given class */
export function getClassDefaultPreferencePaths(rc: ReflectionClass): PreferencePath[] {
	const pathMap = csi.hashArray(csiCard.getAttributeCardKeysForView(rc.type), (key) => key.extendedPath?.getStandardPath() || "", Boolean);
	const validPaths = compact(Object.keys(pathMap));
	return validPaths.map((path) => extend(new PreferencePathConstructor(), { m_path: path }));
}

/** return default property view for given class with optional image spec (the image is not automatically populated for property) */
export function getClassDefaultPropertyView(rc: ReflectionClass, params?: Partial<PropertyView>): PropertyView {
	const defView = create(null, "Property", rc.type) as PropertyView;
	defView.m_id = "__Property__";
	defView.m_rcRoot = rc;

	if (params) {
		Object.assign(defView, params);
	}

	return defView;
}

/** return default property view for given class with only the name attribute visible */
export function getClassEmptyPropertyView(rc: ReflectionClass): PropertyView {
	// lookup existing property view to extract any necessary preference paths
	const ID_PROPERTIES_ALIAS = "__Properties__";
	const ID_PROPERTIES = "Properties";
	const { type } = rc;
	let sourceView = (def.getClassViewDef(rc, ID_PROPERTIES_ALIAS) || def.getClassViewDef(rc, ID_PROPERTIES)) as PropertyView;

	if (sourceView) {
		// changing Cards to Headings first if any
		sourceView.populateHeadingsFromCards(rc);

		sourceView.m_headings = sourceView.m_headings.filter((h) => {
			// filter out non-attribute headings and name heading for result node
			if (!h.m_id || h.isNameWithoutPath) {
				return false;
			}

			// force all other headings to be hidden by default
			h.m_bHidden = true;
			return true;
		});

		// remove any custom toolbar settings
		sourceView.defToolbar = null;
	} else {
		// create new property view if default one not found
		sourceView = create(null, "Property", type) as PropertyView;
		sourceView.m_headings = [];
	}

	// set image and add back name property heading
	const nameHeading = extend(sourceView.newHeading(), { m_prop: xml.PROP_NAME, m_link: "" });
	sourceView.m_headings.unshift(nameHeading);

	// set other view options
	const defaultPrefPaths = getClassDefaultPreferencePaths(rc);
	if (sourceView.prefPaths) {
		sourceView.prefPaths = defaultPrefPaths.concat(sourceView.prefPaths);
	} else {
		sourceView.prefPaths = defaultPrefPaths;
	}

	sourceView.m_id = ID_PROPERTIES_ALIAS;
	sourceView.m_rcRoot = rc;

	// FIX: may need to eventually move these to "params" as was done in getClassDefaultPropertyView - not all cases may want these same parameters
	sourceView.bReadOnly = true;
	sourceView.supportsAttributeGroups = false;

	return sourceView;
}

/** return table view with given target class */
export function getClassDefaultTableView(targetClass: ReflectionClass, sourceClass?: ReflectionClass, pathHintsId?: string, customViewContainerKey?: string): TableView {
	const { type } = targetClass;
	const relationship = new TableRelationshipConstructor();
	relationship.pathHintsContext = configPreferences.getContextualPathHints(pathHintsId);
	relationship.m_prefPaths = getClassDefaultPreferencePaths(targetClass);
	relationship.m_target = type;

	const defView = create(null, "Table", sourceClass?.type || type) as TableView;
	defView.m_id = TABLE_ID;
	defView.m_relationships = [relationship];
	defView.m_rcRoot = sourceClass || targetClass;
	defView.prefKey = customViewContainerKey;

	return defView;
}

/** return view definition to use for overall layout of nodes of given type */
export function getLayoutView(node: C8Node, revisionNode?: C8Node): View | null {
	const viewId = def.getHostViewId();
	const isTemplate = reflectionHints.isTemplateNode(node);
	const rootNode = revisionNode || node;
	const defView = def.getNodeViewDef(rootNode, viewId) as View;

	if (defView && isTemplate) {
		// if there is no layout view for the type, then do not allow one for templates
		// i.e. if supplier cannot navigate to normal node, then should not be able to navigate to template either
		const templateView = def.getNodeViewDef(rootNode, TEMPLATE_VIEW_ID) as View;
		if (templateView) {
			// assign id from layout view so that view access is treated the same
			templateView.m_id = viewId;
			return templateView;
		}
	}

	return defView;
}

/** return type definition owning view for given class */
export function getTypeDefWithView(rc: ReflectionClass, viewId: string): Type | null {
	return def.getClassTypeDef(rc, (t) => t.hasViewDef(viewId));
}

/** return boolean indicating if there is a form definition for the given node type */
export function getFormViewForType(type: C8NodeType): View | undefined {
	const rc = getClass(type);
	if (rc) {
		return def.getClassViewDef(rc.isRevision() ? rc.getMasterClass() : rc, def.DEFAULT_FORM_ID) as View;
	}
}

/** return boolean indicating if there is a form definition for the given node type */
export function hasFormDefinitionForType(type: C8NodeType): boolean {
	const rc = getClass(type);
	return !!rc && !!def.getClassViewDef(rc.isRevision() ? rc.getMasterClass() : rc, def.DEFAULT_FORM_ID, false, true);
}

/** return list of attribute keys used in custom view */
export function loadCustomViewAttributeKeys(customView: C8Node): Pick<ICustomViewOptions, "attributeKeys" | "hiddenKeys"> {
	// NOTE: this does not run the keys through any migration code, so make sure to only use for simple use cases where 
	// attribute groups and matrices(pre 7.9.1) are not a concern
	if (!customView) {
		return {};
	}

	const customOptions = [
		{ name: "attributeKeys", value: [] },
		{ name: "hiddenKeys", value: [] },
	];

	const pv = new PreferenceView({
		customOptions,
		urlNode: customView.$URL,
	});

	pv.load(customView);

	const { attributeKeys, hiddenKeys } = pv.values;
	return { attributeKeys, hiddenKeys };
}

/**
 * Extract an attribute value from a starting node using a path.
 * The returned value can be used for sorting
 * @param cache cache of nodes
 * @param attr an attribute, which could be a ref to another node
 * @param path a path to the node with 'attr' attribute
 * @return a function extracting a value from a node
 */
export function extractSortValue(cache: ICache, node: C8Node, attr: C8AttributeId, path?: string): C8AttributeValue {
	const pathNode = path ? cache.getPathNode(node, path) : node;
	const value = pathNode?.[attr] || "";
	const refNode = cache.get(value);
	return refNode ? locale.getNodeName(refNode, cache) : value;
}

/** determine if a node type should default to the React form */
export function defaultToReactForm(nodeType: C8NodeType): boolean {
	return hasFormDefinitionConfigNodeForType(nodeType) && !hasFormDefinitionForType(nodeType);
}

/** determine if a node type should use the React form */
export function shouldUseReactForm(nodeType: C8NodeType): boolean {
	return defaultToReactForm(nodeType) || hasReactFormForType(nodeType);
}